import React, { useContext, useEffect, useRef, useState } from 'react';
import { useComponentMount, useEmptyCallback } from 'utilities/hooks.util';
import {
  LocalStoreContext,
  LocalDispatchContext
} from '../../localStore/context';
import {
  SkeletonText,
  Button,
  TextInput,
  RadioButtonSkeleton,
  RadioButtonGroup,
  RadioButton,
  Toggle,
  InlineLoading,
  DatePicker,
  DatePickerInput
} from 'carbon-components-react';
import { Close20 } from '@carbon/icons-react';
import {
  getUserDetailsStart,
  getOrganisationGroupsStart,
  createOrganisationStart,
  createOrganisationReset,
  getUsersReset
} from '../../localStore/actions';
import { phoneNumberFormatter } from 'utilities/validation';
import { BYDAPremiumFeaturesInfo, FormError } from '../../../../components';
import { useFormik } from 'formik';
import { NewOrganisationSchema } from '../../../../utilities/validation/newOrganisation.validation';
import Utils from '../../../../utilities/helper.util';

type NewOrgFormValues = {
  orgName: string;
  masterAdminFirstName: string;
  masterAdminLastName: string;
  masterAdminEmail: string;
  hasCreateEnquiry: boolean;
  orgGroup?: number;
  subscriptionStart?: string;
  subscriptionEnd?: string;
  contractRenewal?: string;
};

const UserDetailsModal: React.FC<Props> = ({
  selectedUserId,
  onClose,
  setToastNotificationData,
  setShowToastNotification
}) => {
  const localStore = useContext(LocalStoreContext);
  const localDispatch = useContext(LocalDispatchContext);
  const [selectedUserData, setSelectedUserData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isUpgrade, setIsUpgrade] = useState<boolean>(false);
  const [submitErrText, setSubmitErrText] = useState<string>('');
  const apiCalled = useRef<boolean>(false);
  const [disableContractRenewalDate, setDisableContractRenewalDate] =
    useState<boolean>(true);
  const {
    touched,
    errors,
    values,
    setFieldValue,
    handleChange,
    handleBlur,
    setFieldTouched,
    isSubmitting,
    setSubmitting,
    handleSubmit
  } = useFormik<NewOrgFormValues>({
    enableReinitialize: true,
    initialValues: {
      orgName: '',
      masterAdminFirstName: '',
      masterAdminLastName: '',
      masterAdminEmail: '',
      hasCreateEnquiry: true,
      subscriptionStart: '',
      subscriptionEnd: '',
      contractRenewal: ''
    },
    validationSchema: NewOrganisationSchema,
    onSubmit: (values: NewOrgFormValues) => {
      const emailDomain = values.masterAdminEmail.split('@')[1];
      const payload = {
        organisationName: values.orgName,
        organisationGroup: values.orgGroup || null,
        hasCreateEnquiry: values.hasCreateEnquiry,
        emailDomain: `@${emailDomain}`,
        emailDomains: [emailDomain],
        User: {
          email: values.masterAdminEmail,
          firstName: values.masterAdminFirstName,
          lastName: values.masterAdminLastName
        },
        subscriptionStart: values.subscriptionStart,
        subscriptionEnd: values.subscriptionEnd,
        contractRenewal: values.contractRenewal
      };
      localDispatch(createOrganisationStart(payload));
    }
  });

  const {
    userDetails,
    getUserDetailsStatus,
    createOrgsStatus,
    getOrgGroupsStatus,
    organisationGroups,
    createOrgsError
  } = localStore;

  const onCheckBoxChange = (value: boolean) => {
    setFieldTouched('hasCreateEnquiry', true);
    setFieldValue('hasCreateEnquiry', value);
  };

  const getUserDetailsData = useEmptyCallback((selectedUserId) => {
    const params = {
      userId: selectedUserId
    };
    localDispatch(getUserDetailsStart(params));
  });

  const handleCloseButtonClick = () => {
    setSelectedUserData(null);
    localDispatch(createOrganisationReset());
    onClose();
  };

  const convertToReadableIndustry = (orgType: string | null) => {
    if (!orgType) {
      return null;
    }

    switch (orgType) {
      case 'const':
        return 'Construction';
      case 'gov':
        return 'Government';
      case 'loc':
        return 'Locator';
      case 'util':
        return 'Utilities';
      case 'other':
        return 'Other';
      default:
        return orgType;
    }
  };

  const onChangeDate = (key: string, val: any, setField?: boolean) => {
    const newDate = Utils.getNewDate(val[0]).format('YYYY-MM-DD');
    setField && setFieldValue(key, newDate, true);
    setField && setFieldTouched(key, true);
  };

  useEffect(() => {
    if (selectedUserId > 0) {
      getUserDetailsData(selectedUserId);
    }
  }, [selectedUserId, getUserDetailsData]);

  useEffect(() => {
    setIsLoading(getUserDetailsStatus !== 'success');
  }, [getUserDetailsStatus, selectedUserData]);

  useEffect(() => {
    if (
      getUserDetailsStatus === 'success' &&
      userDetails &&
      Object.keys(userDetails).length !== 0
    ) {
      const userData = {
        fullName: `${userDetails.first_name}${userDetails.last_name}`,
        emailAddress: userDetails.reg_email,
        contactNumber: userDetails.phone_number,
        companyName: userDetails.trial_organisation_name,
        companySize: userDetails.organisation_size,
        industry: userDetails.organisation_type,
        jobTitle: userDetails.job_title,
        monthlyEstimatedEnquiries: userDetails.estimate_enquiry,
        enquiriesLast30Days: userDetails.Enquiry?.length
      };
      setSelectedUserData(userData);
    }
  }, [userDetails, getUserDetailsStatus]);

  useEffect(() => {
    if (
      isUpgrade &&
      getUserDetailsStatus === 'success' &&
      userDetails &&
      Object.keys(userDetails).length !== 0
    ) {
      setFieldValue('masterAdminFirstName', userDetails.first_name);
      setFieldValue('masterAdminLastName', userDetails.last_name);
      setFieldValue('masterAdminEmail', userDetails.reg_email);
    }
  }, [isUpgrade, getUserDetailsStatus, userDetails, setFieldValue]);

  useEffect(() => {
    if (values.subscriptionStart && values.subscriptionEnd) {
      setDisableContractRenewalDate(false);
    }
  }, [values]);

  useEffect(() => {
    if (createOrgsStatus === 'creating') {
      apiCalled.current = true;
    }

    if (isSubmitting && apiCalled.current && createOrgsStatus === 'initial') {
      apiCalled.current = false;
      submitErrText ? setSubmitting(false) : onClose();
    }
  }, [isSubmitting, createOrgsStatus, setSubmitting, onClose, submitErrText]);

  useEffect(() => {
    createOrgsError && setSubmitErrText(createOrgsError);
  }, [createOrgsError]);

  useEffect(() => {
    if (createOrgsStatus === 'success') {
      localDispatch(getUsersReset());
    }
  }, [createOrgsStatus, localDispatch]);

  useEffect(() => {
    if (createOrgsError) {
      setSubmitErrText(createOrgsError);
      setToastNotificationData({
        type: 'add',
        kind: 'error',
        subtitle: createOrgsError
      });
      setShowToastNotification(true);
    }
  }, [
    createOrgsError,
    setShowToastNotification,
    setToastNotificationData,
    values
  ]);

  useEffect(() => {
    if (createOrgsStatus === 'success') {
      const fullName = `${values.masterAdminFirstName} ${values.masterAdminLastName}`;
      setToastNotificationData &&
        setToastNotificationData({
          type: 'add',
          kind: 'success',
          subtitle: `${fullName} (${values.masterAdminEmail}) successfully upgraded`
        });
      setShowToastNotification(true);
      onClose();
    }
  }, [
    createOrgsStatus,
    setToastNotificationData,
    values,
    setShowToastNotification,
    onClose
  ]);

  useComponentMount(() => {
    localDispatch(getOrganisationGroupsStart());
  });

  const subscriptionStartMax = values.subscriptionEnd
    ? Utils.toLocalDateFormat(values.subscriptionEnd)
    : '';
  const subscriptionEndMin = values.subscriptionStart
    ? Utils.toLocalDateFormat(values.subscriptionStart)
    : '';
  const contractRenewalMin = values.subscriptionStart
    ? Utils.toLocalDateFormat(values.subscriptionStart)
    : '';
  const contractRenewalMax = values.subscriptionEnd
    ? Utils.toLocalDateFormat(values.subscriptionEnd)
    : '';

  return (
    <div className="sys-admin-free-plan-users-details-modal">
      <div className="sys-admin-free-plan-users-details-modal-panel">
        <div className="header">
          <div className="panel-title">
            {isUpgrade ? 'Upgrade to organisation' : 'Free plan user details'}
          </div>
          <div className="close">
            <Close20 onClick={handleCloseButtonClick} />
          </div>
        </div>
        {!isUpgrade ? (
          <div className="body">
            <div className="section-container">
              <div className="body-title">Full name</div>
              <div className="body-data">
                {!isLoading ? selectedUserData.fullName : <SkeletonText />}
              </div>
            </div>
            <div className="section-container">
              <div className="body-title">Email address</div>
              <div className="body-data">
                {!isLoading ? selectedUserData.emailAddress : <SkeletonText />}
              </div>
            </div>
            <div className="section-container">
              <div className="body-title">Contact number</div>
              <div className="body-data">
                {!isLoading ? (
                  selectedUserData.contactNumber ? (
                    phoneNumberFormatter({
                      phoneNumber: selectedUserData.contactNumber
                    }).getNationalNumberFormat()
                  ) : (
                    '-'
                  )
                ) : (
                  <SkeletonText />
                )}
              </div>
            </div>
            <div className="section-container">
              <div className="body-title">Company name</div>
              <div className="body-data">
                {!isLoading ? (
                  selectedUserData.companyName ? (
                    selectedUserData.companyName
                  ) : (
                    '-'
                  )
                ) : (
                  <SkeletonText />
                )}
              </div>
            </div>
            <div className="section-container">
              <div className="body-title">Company size</div>
              <div className="body-data">
                {!isLoading ? (
                  selectedUserData.companySize ? (
                    selectedUserData.companySize + ' staff'
                  ) : (
                    '-'
                  )
                ) : (
                  <SkeletonText />
                )}
              </div>
            </div>
            <div className="section-container">
              <div className="body-title">Industry</div>
              <div className="body-data">
                {!isLoading ? (
                  selectedUserData.industry ? (
                    convertToReadableIndustry(selectedUserData.industry)
                  ) : (
                    '-'
                  )
                ) : (
                  <SkeletonText />
                )}
              </div>
            </div>
            <div className="section-container">
              <div className="body-title">Job title</div>
              <div className="body-data">
                {!isLoading ? (
                  selectedUserData.jobTitle ? (
                    selectedUserData.jobTitle
                  ) : (
                    '-'
                  )
                ) : (
                  <SkeletonText />
                )}
              </div>
            </div>
            <div className="section-container">
              <div className="body-title">Estimated enquiries per month</div>
              <div className="body-data">
                {!isLoading ? (
                  selectedUserData.monthlyEstimatedEnquiries !== null ? (
                    selectedUserData.monthlyEstimatedEnquiries + ' enquiries'
                  ) : (
                    '-'
                  )
                ) : (
                  <SkeletonText />
                )}
              </div>
            </div>
            <div className="section-container">
              <div className="body-title">Enquiries last 30 days</div>
              <div className="body-data">
                {!isLoading ? (
                  selectedUserData.enquiriesLast30Days > 0 ? (
                    selectedUserData.enquiriesLast30Days +
                    ` ${
                      selectedUserData.enquiriesLast30Days > 1
                        ? 'enquiries'
                        : 'enquiry'
                    } `
                  ) : (
                    'None'
                  )
                ) : (
                  <SkeletonText />
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="body">
            <div className="new-form-group">
              <TextInput
                id="orgName"
                name="orgName"
                labelText="Organisation name"
                data-testid="orgName"
                placeholder="Enter organisation name"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.orgName}
              />
              {errors.orgName && touched.orgName && (
                <FormError>{errors.orgName}</FormError>
              )}
            </div>
            <div className="new-form-group">
              <TextInput
                id="masterAdminFirstName"
                name="masterAdminFirstName"
                labelText="Master administrator first name"
                data-testid="masterAdminFirstName"
                placeholder="Enter first name"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.masterAdminFirstName}
                readOnly={true}
              />
              {errors.masterAdminFirstName && touched.masterAdminFirstName && (
                <FormError>{errors.masterAdminFirstName}</FormError>
              )}
            </div>
            <div className="new-form-group">
              <TextInput
                id="masterAdminLastName"
                name="masterAdminLastName"
                labelText="Master administrator last name"
                data-testid="masterAdminLastName"
                placeholder="Enter last name"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.masterAdminLastName}
                readOnly={true}
              />
              {errors.masterAdminLastName && touched.masterAdminLastName && (
                <FormError>{errors.masterAdminLastName}</FormError>
              )}
            </div>
            <div className="new-form-group">
              <TextInput
                id="masterAdminEmail"
                name="masterAdminEmail"
                labelText="Master administrator email address"
                data-testid="masterAdminEmail"
                placeholder="Enter email address"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.masterAdminEmail}
                readOnly={true}
              />
              {errors.masterAdminEmail && touched.masterAdminEmail && (
                <FormError>{errors.masterAdminEmail}</FormError>
              )}
            </div>
            <div className="new-form-group">
              {getOrgGroupsStatus === 'fetching' ? (
                <RadioButtonSkeleton />
              ) : (
                <RadioButtonGroup
                  legendText="Organisation group"
                  name="category"
                  orientation="vertical"
                  valueSelected={values.orgGroup}
                  onChange={(value) => {
                    setFieldValue('orgGroup', Number(value), true);
                    setFieldTouched('orgGroup', true, false);
                  }}
                >
                  {organisationGroups.map((og, index) => (
                    <RadioButton
                      key={index}
                      labelText={og.name}
                      value={og.id}
                      id={`org-group-${og.id}`}
                    />
                  ))}
                </RadioButtonGroup>
              )}
            </div>
            <div className="new-form-group subscription-date">
              <div className="date-picker-container">
                <DatePicker
                  dateFormat="d/m/Y"
                  datePickerType="single"
                  className="date-picker"
                  maxDate={subscriptionStartMax}
                  onChange={(val: any) => {
                    onChangeDate('subscriptionStart', val, true);
                  }}
                  value={
                    values.subscriptionStart
                      ? Utils.toLocalDateFormat(values.subscriptionStart)
                      : ''
                  }
                >
                  <DatePickerInput
                    id="subscriptionStart"
                    name="subscriptionStart"
                    data-testid="subscriptionStart"
                    labelText="Subscription start date"
                    placeholder="dd/mm/yyyy"
                    type="text"
                    autoComplete="off"
                  />
                </DatePicker>
                {errors.subscriptionStart && touched.subscriptionStart && (
                  <FormError>{errors.subscriptionStart}</FormError>
                )}
              </div>
              <div className="date-picker-container">
                <DatePicker
                  dateFormat="d/m/Y"
                  datePickerType="single"
                  className="date-picker"
                  minDate={subscriptionEndMin}
                  onChange={(val: any) => {
                    onChangeDate('subscriptionEnd', val, true);
                  }}
                  value={
                    values.subscriptionEnd
                      ? Utils.toLocalDateFormat(values.subscriptionEnd)
                      : ''
                  }
                >
                  <DatePickerInput
                    id="subscriptionEnd"
                    name="subscriptionEnd"
                    data-testid="subscriptionEnd"
                    labelText="Subscription end date"
                    placeholder="dd/mm/yyyy"
                    type="text"
                    autoComplete="off"
                  />
                </DatePicker>
                {errors.subscriptionEnd && touched.subscriptionEnd && (
                  <FormError>{errors.subscriptionEnd}</FormError>
                )}
              </div>
            </div>
            <div className="new-form-group">
              <div className="date-picker-container">
                <DatePicker
                  dateFormat="d/m/Y"
                  datePickerType="single"
                  className="date-picker"
                  minDate={contractRenewalMin}
                  maxDate={contractRenewalMax}
                  onChange={(val: any) => {
                    onChangeDate('contractRenewal', val, true);
                  }}
                  value={
                    values.contractRenewal
                      ? Utils.toLocalDateFormat(values.contractRenewal)
                      : ''
                  }
                >
                  <DatePickerInput
                    id="contractRenewal"
                    name="contractRenewal"
                    data-testid="contractRenewal"
                    labelText="Contract renewal date"
                    placeholder="dd/mm/yyyy"
                    type="text"
                    autoComplete="off"
                    disabled={disableContractRenewalDate}
                  />
                </DatePicker>
                {errors.contractRenewal && touched.contractRenewal && (
                  <FormError>{errors.contractRenewal}</FormError>
                )}
              </div>
            </div>
            <div className="new-form-group">
              <Toggle
                id="hasCreateEnquiry"
                size="sm"
                aria-label="Enable"
                labelA="Enable"
                labelB="Enable"
                labelText="BYDA Premium Features"
                checked={values.hasCreateEnquiry}
                onToggle={(show) => onCheckBoxChange(show)}
              />
            </div>
            {values.hasCreateEnquiry && <BYDAPremiumFeaturesInfo />}
          </div>
        )}
        <div className="footer">
          {isUpgrade ? (
            <div>
              <div className="confirmation-buttons">
                <Button
                  onClick={() => {
                    handleSubmit();
                    setSubmitErrText('');
                  }}
                  disabled={
                    Utils.isObjectEmpty(touched) ||
                    !Utils.isObjectEmpty(errors) ||
                    isSubmitting
                  }
                >
                  {createOrgsStatus === 'initial' ? (
                    'Upgrade'
                  ) : (
                    <InlineLoading
                      className="success-message"
                      description={
                        createOrgsStatus === 'creating'
                          ? 'Upgrading'
                          : createOrgsStatus === 'success'
                          ? 'Upgraded'
                          : 'Failed'
                      }
                      status={
                        createOrgsStatus === 'success'
                          ? 'finished'
                          : createOrgsStatus === 'failed'
                          ? 'error'
                          : 'active'
                      }
                    />
                  )}
                </Button>
                <Button
                  kind="secondary"
                  onClick={() => handleCloseButtonClick()}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button disabled={isLoading} onClick={() => setIsUpgrade(true)}>
              Upgrade this plan
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

type Props = {
  selectedUserId: number;
  onClose: Function;
  setToastNotificationData: Function;
  setShowToastNotification: Function;
};

export default React.memo(UserDetailsModal);
